import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Phone, CheckCircle2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { addBooking, resetBooking } from '../../slice/bookingSlice';

const CategoryBookingPopup = ({ isOpen, onClose, category, itemName }) => {
  const dispatch = useDispatch();
  const { success: submitted, loading: submitting } = useSelector((state) => state.booking);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    travelers: '1',
    arrivalDate: '',
    requirements: ''
  });
  
  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({ ...prev, requirements: `Interested in ${category}: ${itemName}` }));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, category, itemName]);

  useEffect(() => {
    if (submitted) {
      setForm({ name: '', phone: '', email: '', city: '', travelers: '1', arrivalDate: '', requirements: '' });
      const timer = setTimeout(() => {
        onClose();
        dispatch(resetBooking());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted, dispatch, onClose]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Required';
    if (!form.phone.trim()) newErrors.phone = 'Required';
    else if (!/^\d{10}$/.test(form.phone)) newErrors.phone = 'Invalid 10-digit number';
    if (!form.email.trim()) newErrors.email = 'Required';
    if (!form.arrivalDate.trim()) newErrors.arrivalDate = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name,
      email: form.email,
      number: form.phone,
      city: form.city,
      travelers: parseInt(form.travelers),
      travelDate: form.arrivalDate,
      message: `${form.requirements}`
    };

    dispatch(addBooking(payload));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100000] overflow-y-auto flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          <div className="relative w-full flex justify-center items-center pointer-events-none min-h-screen my-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 40 }}
              className="relative w-full max-w-[900px] bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col md:flex-row border border-gray-100 pointer-events-auto"
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/10 backdrop-blur-md hover:bg-orange-main hover:text-white flex items-center justify-center text-gray-600 transition-all duration-300 shadow-lg"
              >
                <X size={20} />
              </button>

              <div className="w-full md:w-2/5 relative h-48 md:h-auto min-h-[250px] md:min-h-full bg-gray-900">
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <div className="inline-flex items-center gap-2 rounded-full bg-orange-main/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] mb-3 shadow-lg text-white">
                    <Sparkles size={12} />
                    Direct Booking
                  </div>
                  <h3 className="text-xl md:text-3xl font-black leading-tight text-white uppercase tracking-tight">
                    Confirm Your <span className="text-orange-main">{category}</span> Booking
                  </h3>
                  <p className="text-gray-400 text-xs font-bold mt-2">{itemName}</p>
                </div>
              </div>

              <div className="w-full md:w-3/5 px-6 py-10 md:px-10 flex flex-col justify-center bg-white">
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Quick Enquiry</h3>
                  <p className="text-xs text-gray-400 mt-1.5 font-bold uppercase tracking-widest">Submit details for backend connectivity</p>
                </div>

                <AnimatePresence mode="wait">
                  {!submitted ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Full Name *"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                        <input
                          type="tel"
                          placeholder="Phone *"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          type="email"
                          placeholder="Email *"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                        <input
                          type="text"
                          placeholder="City/State"
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          type="date"
                          min={today}
                          value={form.arrivalDate}
                          onChange={(e) => setForm({ ...form, arrivalDate: e.target.value })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                        <input
                          type="number"
                          placeholder="Travelers"
                          value={form.travelers}
                          onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all"
                        />
                      </div>
                      <textarea
                        rows={3}
                        value={form.requirements}
                        onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                        className="w-full border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold bg-gray-50/50 focus:border-orange-main focus:bg-white outline-none transition-all resize-none"
                      />
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-orange-main text-white font-black text-xs py-4 rounded-2xl shadow-xl hover:bg-orange-600 transition-all uppercase tracking-widest flex items-center justify-center gap-3"
                      >
                        <Phone size={16} />
                        {submitting ? 'Connecting...' : 'Send Enquiry'}
                      </button>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-5 shadow-lg">
                        <CheckCircle2 size={40} className="text-white" />
                      </div>
                      <h4 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Successfully Sent!</h4>
                      <p className="text-sm text-gray-500 font-bold mt-2">Our team will connect with you shortly regarding your booking.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CategoryBookingPopup;
